/**
 * Hiring Requests — department-head intake form + HR review queue.
 *
 * Two main views, role-gated:
 *  - "My requests" — any user with role ≥ assistant_manager can raise a request
 *  - "HR review queue" — HR sees pending/all requests with approve/reject/revise actions
 *
 * Workflow: requester submits → HR decides (approve → JobRequisition auto-
 * created and linked; reject → terminal with reason; request_revision →
 * requester edits + the edit auto-resubmits to HR).
 */
import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Briefcase, Plus, CheckCircle, XCircle, ClockClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DateInput } from "../components/ui/date-input";

const HR_ROLES = ["super_admin", "company_admin", "country_head", "region_head", "hr_manager"];
const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

const STATUS_META = {
  pending:            { label: "Pending HR review", color: "bg-amber-100 text-amber-800" },
  approved:           { label: "Approved",          color: "bg-emerald-100 text-emerald-800" },
  rejected:           { label: "Rejected",          color: "bg-rose-100 text-rose-800" },
  revision_requested: { label: "Revision requested", color: "bg-blue-100 text-blue-800" },
  cancelled:          { label: "Cancelled",         color: "bg-zinc-200 text-zinc-700" },
};

const fmtINR = (v) => v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

export default function HiringRequests() {
  const { user } = useAuth();
  const isHR = HR_ROLES.includes(user?.role);
  const [activeTab, setActiveTab] = useState(isHR ? "queue" : "mine");
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(null);  // null | "new" | existing record
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/hiring-requests?mine=true")];
      if (isHR) {
        calls.push(api.get("/hiring-requests"));
        calls.push(api.get("/hiring-requests/summary/stats"));
      }
      const results = await Promise.all(calls);
      setMine(results[0].data || []);
      if (isHR) {
        setQueue(results[1].data || []);
        setStats(results[2].data || null);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onSaved = () => { setComposing(null); load(); };
  const onDecided = () => { setViewing(null); load(); };

  if (loading) {
    return <AppShell><div className="p-8 text-zinc-500">Loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="hiring-requests-page">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Briefcase size={22} /> Hiring Requests
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Department heads raise hiring needs here. HR reviews them, and on approval an open requisition is created automatically.
            </p>
          </div>
          <Button onClick={() => setComposing("new")} data-testid="hr-new-btn">
            <Plus size={16} className="mr-1"/> New hiring request
          </Button>
        </header>

        {isHR && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="hr-stats">
            {["pending", "approved", "rejected", "revision_requested", "cancelled"].map(k => (
              <div key={k} className={`p-3 rounded border ${STATUS_META[k].color}`}>
                <div className="text-xs uppercase tracking-wide opacity-80">{STATUS_META[k].label}</div>
                <div className="text-2xl font-semibold mt-1">{stats[k] || 0}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="hr-tab-mine">My requests ({mine.length})</TabsTrigger>
            {isHR && <TabsTrigger value="queue" data-testid="hr-tab-queue">HR queue ({queue.length})</TabsTrigger>}
          </TabsList>
          <TabsContent value="mine" className="mt-4">
            <RequestTable rows={mine} onOpen={setViewing} emptyText="You haven't raised any hiring requests yet." />
          </TabsContent>
          {isHR && (
            <TabsContent value="queue" className="mt-4">
              <RequestTable rows={queue} onOpen={setViewing} showRequester emptyText="Inbox zero — no pending hiring requests." />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {composing && (
        <ComposeDialog
          existing={composing === "new" ? null : composing}
          onClose={() => setComposing(null)}
          onSaved={onSaved}
        />
      )}
      {viewing && (
        <DetailDialog
          requestId={viewing.id}
          onClose={() => setViewing(null)}
          onChanged={onDecided}
          onEdit={(r) => { setViewing(null); setComposing(r); }}
        />
      )}
    </AppShell>
  );
}

/* ---------------------- Table ---------------------- */
function RequestTable({ rows, onOpen, showRequester, emptyText }) {
  if (rows.length === 0) {
    return <Card className="p-8 text-sm text-zinc-500 text-center" data-testid="hr-empty">{emptyText}</Card>;
  }
  return (
    <Card className="p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50">
          <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
            <th className="py-2.5 px-4 font-medium">Code</th>
            <th className="py-2.5 px-2 font-medium">Title</th>
            <th className="py-2.5 px-2 font-medium">Openings</th>
            <th className="py-2.5 px-2 font-medium">Priority</th>
            {showRequester && <th className="py-2.5 px-2 font-medium">Requester</th>}
            <th className="py-2.5 px-2 font-medium">Status</th>
            <th className="py-2.5 px-2 font-medium">Raised</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const s = STATUS_META[r.status] || STATUS_META.pending;
            return (
              <tr
                key={r.id}
                onClick={() => onOpen(r)}
                className="border-b last:border-0 hover:bg-zinc-50 cursor-pointer"
                data-testid={`hr-row-${r.id}`}
              >
                <td className="py-3 px-4 font-mono text-xs text-zinc-600">{r.code}</td>
                <td className="py-3 px-2">
                  <div className="font-medium flex items-center gap-2">
                    {r.title}
                    {r.from_client && r.client_account_name && (
                      <Badge className="text-[9px] bg-indigo-100 text-indigo-800" title={`Raised by ${r.client_account_name} via client portal`}>
                        Client · {r.client_account_name}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">{r.department || "—"}{r.location ? ` · ${r.location}` : ""}</div>
                </td>
                <td className="py-3 px-2">{r.openings}</td>
                <td className="py-3 px-2">
                  <Badge className={`text-[10px] ${r.priority === "urgent" ? "bg-rose-100 text-rose-700" : r.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-700"}`}>
                    {r.priority || "medium"}
                  </Badge>
                </td>
                {showRequester && <td className="py-3 px-2 text-xs">{r.requested_by_name}</td>}
                <td className="py-3 px-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${s.color}`}>{s.label}</span>
                </td>
                <td className="py-3 px-2 text-xs text-zinc-500">{fmtDate(r.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

/* ---------------------- Compose dialog ---------------------- */
function ComposeDialog({ existing, onClose, onSaved }) {
  const isNew = !existing;
  const [form, setForm] = useState(() => existing ? {
    title: existing.title || "",
    department: existing.department || "",
    location: existing.location || "",
    employment_type: existing.employment_type || "full_time",
    work_mode: existing.work_mode || "wfo",
    openings: existing.openings || 1,
    priority: existing.priority || "medium",
    experience_min: existing.experience_min || "",
    experience_max: existing.experience_max || "",
    salary_min: existing.salary_min || "",
    salary_max: existing.salary_max || "",
    currency: existing.currency || "INR",
    description_markdown: existing.description_markdown || "",
    business_justification: existing.business_justification || "",
    skills: (existing.skills || []).join(", "),
    target_join_date: existing.target_join_date || "",
  } : {
    title: "", department: "", location: "",
    employment_type: "full_time", work_mode: "wfo", openings: 1, priority: "medium",
    experience_min: "", experience_max: "", salary_min: "", salary_max: "", currency: "INR",
    description_markdown: "", business_justification: "", skills: "", target_join_date: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        openings: parseInt(form.openings, 10) || 1,
        experience_min: form.experience_min === "" ? null : Number(form.experience_min),
        experience_max: form.experience_max === "" ? null : Number(form.experience_max),
        salary_min:     form.salary_min     === "" ? null : Number(form.salary_min),
        salary_max:     form.salary_max     === "" ? null : Number(form.salary_max),
        skills: form.skills.split(",").map(s => s.trim()).filter(Boolean),
        target_join_date: form.target_join_date || null,
      };
      // Strip nulls on PATCH so we don't clobber unspecified fields
      if (existing) {
        await api.patch(`/hiring-requests/${existing.id}`, payload);
      } else {
        await api.post("/hiring-requests", payload);
      }
      toast.success(existing ? "Request updated" : "Hiring request submitted to HR");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="hr-compose">
        <DialogHeader>
          <DialogTitle>{isNew ? "New hiring request" : `Edit — ${existing.code}`}</DialogTitle>
          <DialogDescription>
            HR reviews and approves. Once approved, an open requisition is created automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-3">
            <Field className="col-span-12" label="Role title *">
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Senior Backend Engineer" data-testid="hr-c-title" />
            </Field>
            <Field className="col-span-6" label="Department">
              <Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="Engineering / Sales / Operations…" data-testid="hr-c-dept" />
            </Field>
            <Field className="col-span-6" label="Location">
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City / branch" data-testid="hr-c-loc" />
            </Field>
            <Field className="col-span-4" label="Openings">
              <Input type="number" min={1} value={form.openings} onChange={(e) => set("openings", e.target.value)} data-testid="hr-c-openings" />
            </Field>
            <Field className="col-span-4" label="Priority">
              <Select value={form.priority} onChange={(v) => set("priority", v)} options={[
                ["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"],
              ]} data-testid="hr-c-priority"/>
            </Field>
            <Field className="col-span-4" label="Target join date">
              <DateInput value={form.target_join_date} onChange={(e) => set("target_join_date", e.target.value)} data-testid="hr-c-doj"/>
            </Field>
            <Field className="col-span-6" label="Employment type">
              <Select value={form.employment_type} onChange={(v) => set("employment_type", v)} options={[
                ["full_time", "Full-time"], ["part_time", "Part-time"], ["contract", "Contract"],
                ["intern", "Intern"], ["consultant", "Consultant"],
              ]} data-testid="hr-c-emptype"/>
            </Field>
            <Field className="col-span-6" label="Work mode">
              <Select value={form.work_mode} onChange={(v) => set("work_mode", v)} options={[
                ["wfo", "Work from office"], ["wfh", "Remote"], ["hybrid", "Hybrid"], ["field", "Field"],
              ]} data-testid="hr-c-mode"/>
            </Field>
            <Field className="col-span-3" label="Exp min (yrs)">
              <Input type="number" min={0} step="0.5" value={form.experience_min} onChange={(e) => set("experience_min", e.target.value)} data-testid="hr-c-expmin"/>
            </Field>
            <Field className="col-span-3" label="Exp max (yrs)">
              <Input type="number" min={0} step="0.5" value={form.experience_max} onChange={(e) => set("experience_max", e.target.value)} data-testid="hr-c-expmax"/>
            </Field>
            <Field className="col-span-3" label="Salary min (₹/yr)">
              <Input type="number" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} data-testid="hr-c-salmin"/>
            </Field>
            <Field className="col-span-3" label="Salary max (₹/yr)">
              <Input type="number" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} data-testid="hr-c-salmax"/>
            </Field>
            <Field className="col-span-12" label="Skills (comma-separated)">
              <Input value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="Python, FastAPI, MongoDB" data-testid="hr-c-skills"/>
            </Field>
            <Field className="col-span-12" label="Role description / JD">
              <Textarea rows={4} value={form.description_markdown} onChange={(e) => set("description_markdown", e.target.value)} placeholder="What will this person do? Outcomes, scope, tech stack." data-testid="hr-c-desc"/>
            </Field>
            <Field className="col-span-12" label="Business justification (why now?)">
              <Textarea rows={3} value={form.business_justification} onChange={(e) => set("business_justification", e.target.value)} placeholder="e.g. Replacing departing tech lead, new product line launch, etc." data-testid="hr-c-just"/>
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="hr-c-submit">
            {saving ? "Saving…" : (existing ? "Save & resubmit" : "Submit to HR")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ className, label, children }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Select({ value, onChange, options, ...rest }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-zinc-300 rounded text-sm bg-white" {...rest}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

/* ---------------------- Detail / decide dialog ---------------------- */
function DetailDialog({ requestId, onClose, onChanged, onEdit }) {
  const { user } = useAuth();
  const isHR = HR_ROLES.includes(user?.role);
  const [r, setR] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/hiring-requests/${requestId}`); setR(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [requestId]); // eslint-disable-line

  const decide = async (decision) => {
    if ((decision === "reject" || decision === "request_revision") && !comment.trim()) {
      toast.error("A comment is required for this action");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/hiring-requests/${requestId}/decide`, { decision, comment: comment || null });
      toast.success({
        approve: "Approved — requisition created",
        reject: "Request rejected",
        request_revision: "Sent back to requester for revision",
      }[decision]);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this hiring request? This can't be undone.")) return;
    setBusy(true);
    try { await api.post(`/hiring-requests/${requestId}/cancel`); toast.success("Cancelled"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!r) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Loading hiring request…</DialogTitle>
            <DialogDescription>Fetching the latest details.</DialogDescription>
          </DialogHeader>
          <div className="py-6 text-center text-sm text-zinc-500">Loading…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const s = STATUS_META[r.status] || STATUS_META.pending;
  const editable = ["pending", "revision_requested"].includes(r.status);
  const decidable = isHR && ["pending", "revision_requested"].includes(r.status);
  const isOwner = r.requested_by_user_id === user?.id;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="hr-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {r.code} · {r.title}
            <span className={`text-[11px] px-2 py-0.5 rounded ${s.color}`}>{s.label}</span>
          </DialogTitle>
          <DialogDescription>
            Raised by {r.requested_by_name} ({r.requested_by_role}) · {fmtDate(r.created_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Cell label="Openings" value={r.openings} />
            <Cell label="Priority" value={r.priority} />
            <Cell label="Department" value={r.department || "—"} />
            <Cell label="Location" value={r.location || "—"} />
            <Cell label="Employment" value={(r.employment_type || "").replace("_", " ")} />
            <Cell label="Mode" value={(r.work_mode || "").toUpperCase()} />
            <Cell label="Experience" value={r.experience_min != null || r.experience_max != null ? `${r.experience_min ?? "?"}–${r.experience_max ?? "?"} yrs` : "—"} />
            <Cell label="Salary band" value={r.salary_min || r.salary_max ? `${fmtINR(r.salary_min)} – ${fmtINR(r.salary_max)}` : "—"} />
            <Cell label="Target join" value={r.target_join_date || "—"} />
            <Cell label="Hiring manager" value={r.hiring_manager_name || "—"} />
          </div>
          {(r.skills || []).length > 0 && (
            <div>
              <div className="text-xs uppercase text-zinc-500 mb-1">Skills</div>
              <div className="flex flex-wrap gap-1">
                {r.skills.map(s => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
              </div>
            </div>
          )}
          {r.description_markdown && (
            <div>
              <div className="text-xs uppercase text-zinc-500 mb-1">Description</div>
              <div className="whitespace-pre-wrap text-zinc-700">{r.description_markdown}</div>
            </div>
          )}
          {r.business_justification && (
            <div>
              <div className="text-xs uppercase text-zinc-500 mb-1">Business justification</div>
              <div className="whitespace-pre-wrap text-zinc-700">{r.business_justification}</div>
            </div>
          )}

          {r.hr_review_comment && (
            <div className={`p-3 rounded border ${r.status === "rejected" ? "bg-rose-50 border-rose-200" : r.status === "revision_requested" ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`}>
              <div className="text-xs uppercase tracking-wide mb-1">HR review</div>
              <div className="text-zinc-700">{r.hr_review_comment}</div>
              <div className="text-[11px] text-zinc-500 mt-1">— {r.hr_reviewed_by_name}, {fmtDate(r.hr_reviewed_at)}</div>
            </div>
          )}

          {r.converted_requisition_id && (
            <div className="p-3 rounded border bg-emerald-50 border-emerald-200">
              <div className="text-sm flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-700"/>
                Converted into requisition <strong>{r.converted_requisition_code}</strong>
              </div>
              <Link
                to={`/app/recruitment/requisitions/${r.converted_requisition_id}`}
                className="text-xs text-emerald-700 underline hover:text-emerald-900"
                onClick={onClose}
              >
                Open the requisition →
              </Link>
            </div>
          )}

          {decidable && (
            <div className="p-3 rounded border bg-zinc-50 space-y-2" data-testid="hr-decide-panel">
              <Label className="text-xs">HR comment (required for reject / request revision)</Label>
              <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything you want the requester to know…" data-testid="hr-decide-comment"/>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={() => decide("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="hr-decide-approve">
                  <CheckCircle size={14} className="mr-1"/> Approve & create requisition
                </Button>
                <Button onClick={() => decide("request_revision")} disabled={busy} variant="outline" data-testid="hr-decide-revise">
                  <ClockClockwise size={14} className="mr-1"/> Request revision
                </Button>
                <Button onClick={() => decide("reject")} disabled={busy} variant="outline" className="text-rose-700 border-rose-300" data-testid="hr-decide-reject">
                  <XCircle size={14} className="mr-1"/> Reject
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row justify-between">
          <div className="space-x-2">
            {editable && (isOwner || isHR) && (
              <Button variant="outline" onClick={() => onEdit(r)} data-testid="hr-detail-edit">Edit</Button>
            )}
            {editable && (isOwner || isHR) && (
              <Button variant="outline" onClick={cancel} disabled={busy} className="text-zinc-700" data-testid="hr-detail-cancel">Cancel request</Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
